import React, { useEffect, useState } from "react";
import { CheckCircle2, Bell, TrendingUp, FileText, Wallet, Plus } from "lucide-react";
import { useCrm } from "../../pages/crm/CrmApp";
import { money, STATUS, fmtDate } from "../../lib/crmApi";
import { ReminderDialog } from "./ReminderDialog";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <Icon className="h-5 w-5" style={{ color: "var(--crm)" }} />
    <p className="mt-2 text-lg font-bold text-slate-900" data-testid={testid}>{value}</p>
    <p className="text-xs text-slate-500">{label}</p>
  </div>
);

export const CrmHome = () => {
  const { api, me, go } = useCrm();
  const [d, setD] = useState(null);
  const [rem, setRem] = useState(false);
  const load = () => api(`/dashboard?tz=${new Date().getTimezoneOffset()}`).then(setD);
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const done = async (r) => {
    await api(`/reminders/${r.id}/done`, { method: "PUT" });
    load();
  };
  if (!d) return null;
  const total = Object.values(d.pipeline).reduce((a, b) => a + b, 0) || 1;
  const overdue = (r) => new Date(`${r.due_at}Z`) < new Date();
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Hola, {me.user.name.split(" ")[0]}</h1>
        <p className="text-sm text-slate-500">Esto es lo que tienes hoy.</p>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat icon={TrendingUp} label={`Ventas del mes (${d.sales_month_count})`} value={money(d.sales_month)} testid="crm-stat-sales" />
        <Stat icon={FileText} label="Cotizaciones abiertas" value={money(d.open_quotes)} testid="crm-stat-quotes" />
        <Stat icon={Wallet} label="Comisiones por pagar" value={money(d.commission_pending)} testid="crm-stat-commission" />
        <Stat icon={Bell} label="Pendientes de hoy" value={d.reminders.length} testid="crm-stat-reminders" />
      </div>
      <section className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Pendientes del día</h2>
          <button onClick={() => setRem(true)} className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold text-white" style={{ background: "var(--crm)" }} data-testid="crm-add-reminder"><Plus className="h-3.5 w-3.5" /> Recordatorio</button>
        </div>
        {!d.reminders.length && <p className="py-6 text-center text-sm text-slate-400" data-testid="crm-no-reminders">Sin pendientes. ¡Buen trabajo!</p>}
        <div className="divide-y divide-slate-100">
          {d.reminders.map((r) => (
            <div key={r.id} className="flex items-center gap-3 py-2.5" data-testid={`crm-reminder-${r.id}`}>
              <button onClick={() => done(r)} className="text-slate-300 hover:text-emerald-500" data-testid={`crm-reminder-done-${r.id}`}><CheckCircle2 className="h-6 w-6" /></button>
              <button className="min-w-0 flex-1 text-left" onClick={() => r.contact_id && go("contacts", r.contact_id)}>
                <p className="truncate text-sm font-medium text-slate-800">{r.text}</p>
                <p className={`text-xs ${overdue(r) ? "text-red-500" : "text-slate-500"}`}>{r.contact_name && `${r.contact_name} · `}{fmtDate(r.due_at)}</p>
              </button>
            </div>
          ))}
        </div>
      </section>
      <section className="rounded-2xl border border-slate-200 bg-white p-4">
        <h2 className="mb-3 font-semibold text-slate-900">Embudo de ventas</h2>
        <div className="space-y-2.5">
          {Object.entries(STATUS).map(([k, s]) => (
            <div key={k} className="flex items-center gap-3 text-sm" data-testid={`crm-pipeline-${k}`}>
              <span className="w-24 shrink-0 text-slate-600">{s.label}</span>
              <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full transition-all" style={{ width: `${(d.pipeline[k] / total) * 100}%`, background: s.color }} />
              </div>
              <span className="w-8 text-right font-semibold text-slate-800">{d.pipeline[k]}</span>
              <span className="hidden w-28 text-right text-xs text-slate-400 sm:block">{money(d.pipeline_values[k])}</span>
            </div>
          ))}
        </div>
      </section>
      <ReminderDialog open={rem} onClose={() => setRem(false)} onSaved={load} />
    </div>
  );
};
